import { CatalogFilters } from '../../hooks/useCatalogFilters';
import { useBabySizeMatches } from '../../hooks/useBabySizeMatches';
import { babyFirstName, childGenderFilter } from '../../utils/babyAge';
import { AGE_VARIATION_TYPE } from '../../utils/variantResolution';

function BabyIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" strokeLinecap="round" strokeLinejoin="round" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 10.5h.01M15 10.5h.01M9.5 14.5c.7.8 1.5 1.2 2.5 1.2s1.8-.4 2.5-1.2M12 3.5c.9-1.2 2.3-1.5 3.2-.8" />
    </svg>
  );
}

function sameValues(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

interface BabySizeChipsProps {
  filters: CatalogFilters;
  layout?: 'bar' | 'inline';
}

export function BabySizeChips({ filters, layout = 'bar' }: BabySizeChipsProps) {
  const matches = useBabySizeMatches();
  const inline = layout === 'inline';

  if (matches.length === 0) return null;

  const chips = (
    <>
        {matches.map(({ baby, options }) => {
          const codes = options.map((option) => option.code);
          const sizeLabel = options.map((option) => option.label).join(' ou ');
          const childGender = childGenderFilter(baby);
          const active =
            filters.variationTypeId === AGE_VARIATION_TYPE &&
            sameValues(filters.variationValues, codes) &&
            filters.gender === childGender;
          return (
            <button
              key={baby.id}
              type="button"
              aria-pressed={active}
              aria-label={`Para ${babyFirstName(baby)}, tamanho ${sizeLabel}`}
              onClick={() =>
                active ? filters.clearChildSize() : filters.handleChildSizeSelect(AGE_VARIATION_TYPE, codes, childGender)
              }
              className={[
                'group shrink-0 inline-flex items-center gap-2 min-h-[44px] rounded-full border pl-1 pr-3 py-1 text-left transition-all',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate focus-visible:ring-offset-2',
                active
                  ? 'bg-chocolate-dark border-chocolate-dark text-white'
                  : 'bg-white border-chocolate-light text-text-dark hover:border-chocolate',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  active ? 'bg-white text-chocolate-dark' : 'bg-chocolate-dark text-white',
                ].join(' ')}
              >
                <BabyIcon className="w-4 h-4" />
              </span>
              <span className="text-sm whitespace-nowrap">
                <span className="font-semibold">Para {babyFirstName(baby)}</span>
                <span className={[active ? 'text-white/90' : 'text-text-medium', inline ? 'hidden' : ''].join(' ')}> · tamanho {sizeLabel}</span>
              </span>
              <span className="shrink-0" aria-hidden="true">
                {active ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-chocolate-dark transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                  </svg>
                )}
              </span>
            </button>
          );
        })}
    </>
  );

  if (inline) return chips;

  return (
    <div className="-mx-4 px-4 sm:mx-0 sm:px-0 mb-4 overflow-x-auto no-scrollbar" aria-label="Tamanhos para as suas crianças" role="group">
      <div className="flex items-center gap-2 w-max sm:w-auto sm:flex-wrap pr-4 sm:pr-0">{chips}</div>
    </div>
  );
}
